import {  Card, Col, Input, Row, Button, Tooltip, Typography } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react'
import UserAPI from 'restAPI/UserAPI';
import _ from 'lodash';
import { UnSuppressIcon } from 'components/Icons'
import TableContainer from "components/TableContainer";
import { BASE_GUTTER } from 'constants/Layout';
import message from "components/CustomMessage";
import BusinessUnitAPI from 'restAPI/BusinessUnitAPI';
import { useHistory, useLocation } from "react-router-dom";
import { clearTemporarySessionStorage } from 'utils/sessionFilterStoreUtil';
import { useActiveUserContext } from 'contexts/ActiveUserContext';

export default function TenantSwitcher() {
    const [tenants, setTenants] = useState([]);
    const [enabled, setEnabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [id, setId] = useState()
    const location = useLocation();
    const [page, setPage] = useState(0);
    const maxSizeReached = useRef(true);
    const timeoutIdRef = useRef();
    const searchTextRef = useRef('');
    const searchInputRef = useRef(null);
    const history = useHistory();
    const activeUser = useActiveUserContext();

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const error = urlParams.get("error");
        const account = urlParams.get("accountId");
        const accountName = _.filter(tenants, { id: +account })[0]?.name;

        if (error === "IpBlocked" && accountName) {
            message.error(`User IP not allowed to access account ${accountName}`)
        }
    }, [location, tenants]);

    const fetchTenants = useCallback((e) => {
        const searchText = e.target.value;
        if (searchTextRef.current !== searchText) {
            searchTextRef.current = searchText;
            setTenants([]);
            // avoiding extra API call, since page is a dependency for this method.
            // It will get call again as state updates.
            if (page > 0) {
                setPage(0);
                return;
            }
        }
        let cancel = _.noop;
        setLoading(true);
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = setTimeout(() => {
            const params = { page, size: 8, sort: 'name'};
            if (searchText) {
                params.name = searchText;
            }
            const promiseObj = UserAPI.getTenants(params);
            cancel = promiseObj.cancel
            promiseObj.promise.then(
                (data) => {
                    setTenants(tenants => {
                        const newList = _.uniqBy([...tenants, ...(data?.content || [])], 'id');
                        // filter the list as per search text
                        // if (searchText) {
                        //     newList = _.filter(newList, (list) => _.toLower(list.name).includes(_.toLower(searchText)));
                        // }
                        
                        maxSizeReached.current = newList.length === data.totalElements;
                        return newList;
                    });
                },
                _.noop
            ).finally(() => setLoading(false))
        }, 500);
        return () => {
            clearTimeout(timeoutIdRef.current);
            cancel();
        };
    }, [page]);

    useEffect(() => {
        return fetchTenants({ target: { value: searchTextRef.current } });
    }, [fetchTenants]);

    const updateTenant =  async (id) => {
        try {
            const closeMessage = message.loading("Switching Account...");
            let { businessUnitId } = await UserAPI.getDefaultBU(id).promise;
            if(!businessUnitId){
                const businessUnits = await BusinessUnitAPI.getByTenantId(id).promise;
                businessUnitId = businessUnits[0].id;
            }
            await UserAPI.updateTenant(id, businessUnitId).promise;
            closeMessage();
            // clear's localstorage while switching the tenants
            clearTemporarySessionStorage();
            // refresh to dashboard when the tenant is changed,
            // so that no data/context gets retained from previous tenant
            window.location.href = "/";
        } catch (e) { 
            console.error(e);
        }
    }

    useEffect(() => {
        const {promise, cancel} = UserAPI.getDefaultAccount()
        promise.then((data) => {
            setId(data.tenantId)
        }).catch(err => {
            console.error(err)
        })
        return cancel
    }, [])

    useEffect(() => {
        // Focus the search input when component mounts
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [])

    const setDefault = async (record, _e) => {
        setEnabled(false);
        const closeLoadMsg = message.loading(`Setting ${record.name} as Default Account`);
        try{
            await UserAPI.setDefaultAccount({
                tenantId: record.id
            }).promise;
            setId(record.id);
        } finally {
            setEnabled(true);
            closeLoadMsg();
        }
    }
    
    const colProps = {
        lg: 10,
        md: 12,
        sm: 16,
        xs: 24,
        style: {margin: "auto", marginTop: "20px"}
    }
    const getDisableSetDefaultButton = (record) => (record.id === id? true : !enabled) || (["Prod", "Sandbox"].indexOf(record.type) !== -1 && activeUser.email.includes("armorcode.io"))

    return (
        <div className="login-page">
            <Row>
                <Col {...colProps}>
                    <Card 
                        bordered={false}
                        className="login-box" 
                        title="Select an Account"
                        extra={<Typography.Link onClick={history.goBack}>Exit Console</Typography.Link>}
                    >
                        <Row gutter={BASE_GUTTER} className="p-sm">
                            <Col span={24}>
                                <Input ref={searchInputRef} placeholder="Search" onChange={fetchTenants} allowClear />
                            </Col>
                        </Row>
                            <TableContainer 
                                loading={loading}
                                dataSource={tenants}
                                showPagination={false}
                                columns={[
                                    {
                                        title: "Account ID",
                                        width: 90,
                                        dataIndex: "id",
                                    },
                                    {
                                        title: "Account Name",
                                        dataIndex: "name",
                                        render: (value, record) => <Tooltip title={`Click to switch to ${value}`}><Button type="link" onClick={() => updateTenant(record.id)}>{value}</Button></Tooltip>
                                    },
                                    {
                                        dataIndex: "id",
                                        width: 137,
                                        render: (value, record) => 
                                            value === id ?
                                            <span><UnSuppressIcon /> Current Default</span>
                                            :
                                            <Button disabled={getDisableSetDefaultButton(record)} type="default" size="small" onClick={(e) => setDefault(record,e)} >
                                                Set as Default
                                            </Button>
                                    }]
                                }
                                locale={
                                    { emptyText: "No Account Available" }
                                  }
                            />
                            {!maxSizeReached.current && <div className='w-100 text-center'><Button disabled={loading} type='link' onClick={() => setPage(page => page + 1)}>+ Show More</Button></div>}
                    </Card>
                </Col>
            </Row>
            
        </div>
    )
}
